// Date Formatting Utilities - GetItDone Scheduler
// Uses Australian locale (en-AU) with DD/MM/YYYY format

/**
 * Format a date string to Australian format (DD/MM/YYYY)
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Formatted date
 */
export function formatDate(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Format a date with month name (DD Mon YYYY)
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Formatted date
 */
export function formatDateLong(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

/**
 * Format a date and time
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Formatted date and time
 */
export function formatDateTime(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format time only (HH:MM AM/PM)
 * @param {string} timeString - Time string (HH:MM or HH:MM:SS)
 * @returns {string} Formatted time
 */
export function formatTime(timeString) {
  if (!timeString) return '';

  // Handle time string without date
  const [hours, minutes] = timeString.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return timeString;

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');

  return `${displayHours}:${displayMinutes} ${period}`;
}

/**
 * Check if a date is today
 * @param {string|Date} dateInput - Date to check
 * @returns {boolean}
 */
export function isToday(dateInput) {
  if (!dateInput) return false;
  const date = new Date(dateInput);
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

/**
 * Check if a date is within this week
 * @param {string|Date} dateInput - Date to check
 * @returns {boolean}
 */
export function isThisWeek(dateInput) {
  if (!dateInput) return false;
  const date = new Date(dateInput);
  const today = new Date();

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);

  return date >= startOfWeek && date < endOfWeek;
}

/**
 * Check if two dates are the same day
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean}
 */
export function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

/**
 * Get the start of the month
 * @param {Date} date - Date
 * @returns {Date}
 */
export function getMonthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * Get the end of the month
 * @param {Date} date - Date
 * @returns {Date}
 */
export function getMonthEnd(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/**
 * Add days to a date
 * @param {Date} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date}
 */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Get day name (Mon, Tue, etc.)
 * @param {Date} date - Date
 * @returns {string}
 */
export function getDayName(date) {
  return date.toLocaleDateString('en-AU', { weekday: 'short' });
}

// ============================================
// Business-timezone helpers (appointments are stored as UTC; the business
// runs in Australia/Brisbane, so render in that zone regardless of device tz).
// ============================================
export const BUSINESS_TZ = 'Australia/Brisbane';

/** Time of a UTC instant in Brisbane, e.g. "9:00 AM". */
export function formatApptTime(utcIso) {
  if (!utcIso) return '';
  return new Date(utcIso).toLocaleTimeString('en-AU', {
    timeZone: BUSINESS_TZ, hour: 'numeric', minute: '2-digit', hour12: true
  });
}

/** Date of a UTC instant in Brisbane, "DD/MM/YYYY". */
export function formatApptDate(utcIso) {
  if (!utcIso) return '';
  return new Date(utcIso).toLocaleDateString('en-AU', {
    timeZone: BUSINESS_TZ, day: '2-digit', month: '2-digit', year: 'numeric'
  });
}

/** Friendly date, "Tue, 23 Jun". */
export function formatApptDateLong(utcIso) {
  if (!utcIso) return '';
  return new Date(utcIso).toLocaleDateString('en-AU', {
    timeZone: BUSINESS_TZ, weekday: 'short', day: 'numeric', month: 'short'
  });
}

/** 'YYYY-MM-DD' key for a UTC instant or Date, in Brisbane local time. */
export function localDateKey(dateOrIso) {
  if (!dateOrIso) return '';
  const d = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso);
  return d.toLocaleDateString('en-CA', { timeZone: BUSINESS_TZ });
}

/** A native Date's 'YYYY-MM-DD' in local time (for input[type=date] / calendar nav). */
export function dateToInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const BRISBANE_OFFSET_MIN = 600; // UTC+10, no DST — must match server/lib/time.js

/** Brisbane-local date 'YYYY-MM-DD' + time 'HH:MM' -> UTC ISO (device-tz independent). */
export function brisbaneInputsToUtc(dateStr, timeStr) {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = (timeStr || '00:00').split(':').map(Number);
  return new Date(Date.UTC(y, mo - 1, d, h, mi) - BRISBANE_OFFSET_MIN * 60000).toISOString();
}

/** UTC ISO -> { date:'YYYY-MM-DD', time:'HH:MM' } in Brisbane local time (for form inputs). */
export function utcToBrisbaneInputs(iso) {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString('en-CA', { timeZone: BUSINESS_TZ }),
    time: d.toLocaleTimeString('en-GB', { timeZone: BUSINESS_TZ, hour12: false, hour: '2-digit', minute: '2-digit' })
  };
}

/** "8:00 am–4:00 pm" for a visit's start and end (Brisbane); just the start when there is no end. */
export function formatApptRange(startIso, endIso) {
  if (!startIso) return '';
  return endIso ? `${formatApptTime(startIso)}–${formatApptTime(endIso)}` : formatApptTime(startIso);
}

/**
 * Get relative time description
 * @param {string|Date} dateInput - Date to compare
 * @returns {string}
 */
export function getRelativeTime(dateInput) {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  const now = new Date();

  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}
